import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import Tabs, { TabPanel, TAB_LAYOUT, TAB_DISPLAY } from "@acadia-ui/tabs";
import Loading from "@acadia-ui/loading";
import Status, { STATUS_DISPLAY, STATUS_TYPE } from "@acadia-ui/status";
import { useAdSet, useCreative, useInsertionOrder, useLineItem, usePackage, usePlacement } from "./AuditLevels";
import { putFilterData } from "../../Flux/Actions/BookFilterActions";
import PackagesDataStore from "../../Flux/Stores/PackagesDataStore";
import { triggerPlacementModal } from "../../Flux/Actions/PlacementActions";
import TableHelper from "../../Components/Table/TableHelper";
import PropTypes from "prop-types";
import EmptyState from "@acadia-ui/empty-state";
import Heading, { HEADING_LEVEL, HEADING_SIZE } from "@acadia-ui/heading";
import Card, { CARD_PADDING } from "@acadia-ui/card";
import Button, { BUTTON_DISPLAY, BUTTON_SIZE } from "@acadia-ui/button";
import { useNavigate } from "react-router-dom";
import stickyHelper from "../ViewMasterCampaigns/utils/stickyHelper";
import Badge from "@acadia-ui/badge";

AuditLevelTabs.propTypes = {
	auditLevels: PropTypes.arrayOf(
		PropTypes.shape({
			AuditLevelName: PropTypes.string.isRequired,
			HierarchyOrder: PropTypes.string.isRequired,
			HierarchyLevelVanityName: PropTypes.string,
			AuditLevelDisplayName: PropTypes.string
		})
	).isRequired,
	metaData: PropTypes.object.isRequired,
	parentCampaignUniqueID: PropTypes.string.isRequired,
	setSelectedAuditLevel: PropTypes.func.isRequired,
	fetchMetaData: PropTypes.func.isRequired,
	JIRAStatus: PropTypes.bool,
	placementAPI: PropTypes.bool,
	defaultTab: PropTypes.number
};

export const TabContext = React.createContext();

export default function AuditLevelTabs({
	// this comment exists to prevent eslint from making this unreadable.
	auditLevels: rawAuditLevels,
	metaData,
	parentCampaignUniqueID,
	setSelectedAuditLevel,
	fetchMetaData,
	JIRAStatus,
	placementAPI,
	defaultTab = 0,
	campaignJiraTicketExists
}) {
	const [selectedTab, setSelectedTab] = useState(undefined);
	const [selectedPackage, setSelectedPackage] = useState(null);

	const auditLevelsAreValidOrLoading = useMemo(() => {
		if (rawAuditLevels === null) {
			return true;
		}
		// levels are valid if they are not "Campaign" and have a non-negative hierarchy order
		const displayLevels = rawAuditLevels?.filter(({ HierarchyOrder, AuditLevelID }) => HierarchyOrder !== "-1" && AuditLevelID !== 17);
		return displayLevels.length > 0;
	}, [rawAuditLevels]);

	// prettier-ignore
	const auditLevels = useMemo(() => {
		return rawAuditLevels
			?.filter(({ HierarchyOrder }) => HierarchyOrder !== "-1")
			?.sort((a, b) => a.HierarchyOrder > b.HierarchyOrder);
	}, [rawAuditLevels]);

	const [counts, updateCounts] = useReducer((prevCounts, hooks) => {
		if (hooks === undefined) {
			return {};
		}

		const newCounts = Object.entries(hooks).reduce((acc, [key, value]) => {
			if (auditLevels?.find(({ AuditLevelName }) => key === AuditLevelName)) {
				acc[key] = value.data?.length;
			}
			return acc;
		}, {});

		if (JSON.stringify(prevCounts) === JSON.stringify(newCounts)) {
			return prevCounts;
		}

		// check if all audit levels are defined, if so, it's finished loading - select the tab
		if (auditLevels?.every(({ AuditLevelName }) => newCounts?.[AuditLevelName] !== undefined) && selectedTab === undefined) {
			setSelectedTab(defaultTab);
		}

		return newCounts;
	}, {});

	// memoized tabs object - passed to the <Tabs> component. defined dynamically to support loading states
	const tabs = useMemo(() => {
		if (auditLevels?.length > 0 && counts) {
			return auditLevels?.map(({ AuditLevelName, HierarchyLevelVanityName, AuditLevelDisplayName }) => {
				if (counts[AuditLevelName] === undefined) {
					return {
						id: AuditLevelName,
						disabled: true,
						panelId: `panel-${parentCampaignUniqueID}-${AuditLevelName}`,
						label: (
							<div style={{ display: "flex", gap: 8 }}>
								<div>{HierarchyLevelVanityName || AuditLevelDisplayName}</div>
								<div className="gos-skeleton gos-skeleton-1-1" style={{ width: 22, height: 22 }} />
							</div>
						)
					};
				}

				return {
					id: AuditLevelName,
					disabled: false,
					label: HierarchyLevelVanityName || AuditLevelDisplayName,
					data: counts[AuditLevelName],
					panelId: `panel-${parentCampaignUniqueID}-${AuditLevelName}`
				};
			});
		}
		return Array.from({ length: 3 }, (_, i) => ({
			disabled: true,
			label: (
				<div style={{ display: "flex", gap: 8 }}>
					<div className="gos-skeleton gos-skeleton-1-1" style={{ width: 75, height: 22 }} />
					<div className="gos-skeleton gos-skeleton-1-1" style={{ width: 22, height: 22 }} />
				</div>
			),
			panelId: `panel-placeholder-${i}`
		}));
	}, [auditLevels, counts, parentCampaignUniqueID]);

	const getAuditLevelHookProps = useCallback(
		(targetAuditLevelName) => ({
			auditLevel: auditLevels?.find(({ AuditLevelName }) => AuditLevelName === targetAuditLevelName),
			parentCampaignUniqueID,
			metaData,
			auditLevels,
			fetchMetaData,
			JIRAStatus,
			placementAPI,
			campaignJiraTicketExists
		}),
		[parentCampaignUniqueID, metaData, auditLevels, fetchMetaData, JIRAStatus, placementAPI, campaignJiraTicketExists]
	);

	const navigateToPlacements = useCallback(
		(newPackage = null) => {
			if (newPackage) {
				setSelectedPackage(newPackage);
			}

			setSelectedTab(auditLevels.findIndex(({ AuditLevelName }) => AuditLevelName === "Placement"));
		},
		[auditLevels]
	);

	const navigateToLineItems = useCallback(() => {
		setSelectedTab(auditLevels.findIndex(({ AuditLevelName }) => AuditLevelName === "LineItem"));
	}, [auditLevels]);

	useEffect(() => {
		if (selectedTab === auditLevels.findIndex(({ AuditLevelName }) => AuditLevelName === "Placement") && PackagesDataStore.getNavigate()) {
			triggerPlacementModal();
		}
	}, [selectedTab]);

	const auditLevelHooks = {
		Package: usePackage({ ...getAuditLevelHookProps("Package"), navigateToPlacements, setSelectedPackage }),
		Placement: usePlacement({ ...getAuditLevelHookProps("Placement"), selectedPackage, setSelectedPackage, selectedTab }),
		AdSet: useAdSet(getAuditLevelHookProps("AdSet")),
		LineItem: useLineItem(getAuditLevelHookProps("LineItem")),
		Creative: useCreative(getAuditLevelHookProps("Creative")),
		InsertionOrder: useInsertionOrder({ ...getAuditLevelHookProps("InsertionOrder"), navigateToLineItems })
	};

	const renderedTabsReducer = useCallback(() => {
		const refreshMap = Object.entries(auditLevelHooks)?.reduce((acc, [name, { refresh }]) => {
			return {
				...acc,
				[name]: async () => {
					await refresh(
						parentCampaignUniqueID,
						auditLevels?.find(({ AuditLevelName }) => name === AuditLevelName)
					);
				}
			};
		}, {});

		return Object.entries(auditLevelHooks)
			?.filter(([name]) => auditLevels?.find(({ AuditLevelName }) => name === AuditLevelName))
			?.map(([AuditLevelName, { Component }]) => {
				return (
					<TabPanel
						// this comment exists to prevent eslint from making this unreadable.
						id={`panel-${parentCampaignUniqueID}-${AuditLevelName}`}
						key={`panel-${parentCampaignUniqueID}-${AuditLevelName}`}
					>
						<Component refreshMap={refreshMap} />
					</TabPanel>
				);
			});
	}, [auditLevelHooks, parentCampaignUniqueID, auditLevels]);

	const [renderedTabs, updateRenderedTabs] = useReducer(renderedTabsReducer, []);

	useEffect(() => {
		updateCounts(auditLevelHooks);
	}, [auditLevelHooks]);

	// if the selected tab's rendered data changes in any way, rerender the tab
	useEffect(() => {
		if (selectedTab !== undefined) {
			updateRenderedTabs();
		}
	}, [
		selectedTab,
		JSON.stringify(auditLevelHooks?.[auditLevels?.[selectedTab]?.AuditLevelName]?.deps || [])
		// TODO: removed renderedData reliance, implement `deps` in each audit level and add renderedData there instead.
		// JSON.stringify(auditLevelHooks?.[auditLevels?.[selectedTab]?.AuditLevelName]?.renderedData || [])
	]);

	useEffect(() => {
		setSelectedTab(undefined);
		updateCounts(undefined);
	}, [parentCampaignUniqueID]);

	// doing this for now... eventually need to move this to CL so tables loaded in tabs have their sticky styles work properly
	// useEffect(() => {
	// 	if (selectedTab !== undefined && auditLevels?.length > 0 && parentCampaignUniqueID) {
	// 		const AuditLevelName = auditLevels?.[selectedTab]?.AuditLevelName || "";
	// 		const el = document.querySelector(`#panel-${parentCampaignUniqueID}-${AuditLevelName} table`);
	// 		stickyHelper(el);
	// 	}
	// }, [selectedTab, auditLevels, parentCampaignUniqueID]);

	useEffect(() => {
		if (auditLevels?.length > 0 && selectedTab !== undefined) {
			putFilterData("auditlevel", { value: auditLevels?.[selectedTab]?.AuditLevelID, label: auditLevels?.[selectedTab]?.AuditLevelName });
			setSelectedAuditLevel(auditLevels?.[selectedTab] || null);
		}
	}, [selectedTab, auditLevels, setSelectedAuditLevel]);

	const navigate = useNavigate();

	// REMOVED: simply redirect home instead (see useEffect above)
	// useEffect(() => {
	// 	if (!auditLevelsAreValidOrLoading) {
	// 		navigate("/");
	// 	}
	// }, [auditLevelsAreValidOrLoading]);

	if (auditLevelsAreValidOrLoading) {
		return (
			<Tabs
				// this comment exists to prevent eslint from making this unreadable.
				layout={TAB_LAYOUT.HORIZONTAL}
				display={TAB_DISPLAY.BUTTON}
				tabs={tabs}
				selectedTab={selectedTab}
				onSelect={(tab) => {
					setSelectedPackage(null);
					setSelectedTab(tab);
				}}
			>
				{auditLevelHooks?.[auditLevels?.[selectedTab]?.AuditLevelName]?.modals || <></>}
				{selectedTab === undefined && <Loading />}
				{renderedTabs}
			</Tabs>
		);
	}
	// return <></>;

	return (
		<div id="invalid-taxonomy-configuration-container">
			<Card id={"invalid-taxonomy-configuration"}>
				<EmptyState
					heading={
						<Heading
							icon="icon-document-missing"
							size={HEADING_SIZE.MEDIUM}
							as={HEADING_LEVEL.H2}
							description={"The taxonomy configuration for this campaign is either missing or invalid. Please contact your system administrator for assistance."}
						>
							Missing Taxonomy Configuration
						</Heading>
					}
				>
					<Button icon={"icon-rotate-ccw"} size={BUTTON_SIZE.SMALL} onClick={() => navigate("/")} data-tracking-id-alt="return-home">
						Return home
					</Button>
				</EmptyState>
			</Card>
		</div>
	);
}
